import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Space,
  Tag,
  Popconfirm,
  message,
  Divider,
  Alert,
  Typography,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  KeyOutlined,
  DollarOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Create axios instance with JWT authentication (for admin)
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const Config = () => {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState({});
  const [providerModal, setProviderModal] = useState({ visible: false, data: null, mode: 'create' });
  const [credentialModal, setCredentialModal] = useState({ visible: false, providerId: null });
  const [feeModal, setFeeModal] = useState({ visible: false, providerId: null, data: null });
  const [settingModal, setSettingModal] = useState({ visible: false, data: null });
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerDetails, setProviderDetails] = useState(null);
  const [form] = Form.useForm();
  const [credentialForm] = Form.useForm();
  const [feeForm] = Form.useForm();
  const [settingForm] = Form.useForm();

  useEffect(() => {
    loadProviders();
    loadSettings();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/config/providers');
      setProviders(response.data.data || []);
    } catch (error) {
      message.error('Failed to load providers');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await adminApi.get('/config/settings');
      setSettings(response.data.data || {});
    } catch (error) {
      message.error('Failed to load settings');
      console.error('Error:', error);
    }
  };

  const loadProviderDetails = async (providerId) => {
    try {
      const response = await adminApi.get(`/config/providers/${providerId}`);
      setProviderDetails(response.data.data);
    } catch (error) {
      message.error('Failed to load provider details');
      console.error('Error:', error);
    }
  };

  const handleCreateProvider = () => {
    setProviderModal({ visible: true, data: null, mode: 'create' });
    form.resetFields();
  };

  const handleEditProvider = (provider) => {
    setProviderModal({ visible: true, data: provider, mode: 'edit' });
    form.setFieldsValue({
      ...provider,
      supported_methods: JSON.parse(provider.supported_methods || '[]'),
      settings: provider.settings ? JSON.stringify(JSON.parse(provider.settings), null, 2) : ''
    });
  };

  const handleDeleteProvider = async (providerId) => {
    try {
      await adminApi.delete(`/config/providers/${providerId}`);
      message.success('Provider deleted successfully');
      loadProviders();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete provider');
    }
  };

  const handleSaveProvider = async (values) => {
    try {
      const payload = {
        ...values,
        supported_methods: values.supported_methods || [],
        settings: values.settings ? JSON.parse(values.settings) : {}
      };

      if (providerModal.mode === 'create') {
        await adminApi.post('/config/providers', payload);
        message.success('Provider created successfully');
      } else {
        await adminApi.put(`/config/providers/${providerModal.data.id}`, payload);
        message.success('Provider updated successfully');
      }

      setProviderModal({ visible: false, data: null, mode: 'create' });
      loadProviders();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save provider');
    }
  };

  const handleAddCredential = (providerId) => {
    setCredentialModal({ visible: true, providerId });
    credentialForm.resetFields();
  };

  const handleSaveCredential = async (values) => {
    try {
      await adminApi.post(`/config/providers/${credentialModal.providerId}/credentials`, values);
      message.success('Credential saved successfully');
      setCredentialModal({ visible: false, providerId: null });
      if (selectedProvider) {
        loadProviderDetails(selectedProvider.id);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save credential');
    }
  };

  const handleDeleteCredential = async (providerId, credentialId) => {
    try {
      await adminApi.delete(`/config/providers/${providerId}/credentials/${credentialId}`);
      message.success('Credential deleted successfully');
      if (selectedProvider) {
        loadProviderDetails(selectedProvider.id);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete credential');
    }
  };

  const handleEditSetting = (setting) => {
    setSettingModal({ visible: true, data: setting });
    settingForm.setFieldsValue(setting);
  };

  const handleSaveSetting = async (values) => {
    try {
      await adminApi.put(`/config/settings/${settingModal.data.setting_key}`, values);
      message.success('Setting updated successfully');
      setSettingModal({ visible: false, data: null });
      loadSettings();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update setting');
    }
  };

  const providerColumns = [
    {
      title: 'Provider',
      key: 'provider',
      render: (_, record) => (
        <div>
          <div className="font-semibold">{record.provider_name}</div>
          <Text type="secondary">{record.provider_code}</Text>
        </div>
      ),
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      key: 'environment',
      render: (env) => (
        <Tag color={env === 'production' ? 'green' : 'orange'}>
          {env.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Credentials',
      dataIndex: 'credential_count',
      key: 'credential_count',
      align: 'center',
    },
    {
      title: 'Fee Configs',
      dataIndex: 'fee_config_count',
      key: 'fee_config_count',
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedProvider(record);
              loadProviderDetails(record.id);
            }}
          >
            Details
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditProvider(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this provider?"
            onConfirm={() => handleDeleteProvider(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const credentialColumns = [
    {
      title: 'Key',
      dataIndex: 'credential_key',
      key: 'credential_key',
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      key: 'environment',
      render: (env) => (
        <Tag color={env === 'production' ? 'green' : 'orange'}>
          {env.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure to delete this credential?"
          onConfirm={() => handleDeleteCredential(selectedProvider.id, record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const renderSettingsTable = (categorySettings, category) => {
    const columns = [
      {
        title: 'Setting',
        dataIndex: 'setting_key',
        key: 'setting_key',
        render: (key, record) => (
          <div>
            <div className="font-semibold">{key}</div>
            <Text type="secondary">{record.description}</Text>
          </div>
        ),
      },
      {
        title: 'Value',
        dataIndex: 'setting_value',
        key: 'setting_value',
        render: (value, record) => {
          if (record.setting_type === 'boolean') {
            return <Tag color={value === 'true' ? 'green' : 'red'}>{value}</Tag>;
          }
          if (record.is_encrypted) {
            return <Text type="secondary">••••••••</Text>;
          }
          return <Text>{value}</Text>;
        },
      },
      {
        title: 'Type',
        dataIndex: 'setting_type',
        key: 'setting_type',
        render: (type) => <Tag>{type}</Tag>,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSetting(record)}
          >
            Edit
          </Button>
        ),
      },
    ];

    return (
      <Card title={category.toUpperCase()} size="small" style={{ marginBottom: 16 }}>
        <Table
          columns={columns}
          dataSource={categorySettings}
          rowKey="setting_key"
          size="small"
          pagination={false}
        />
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Arendo Configuration</Title>
        <Text type="secondary">Manage providers, credentials, and system settings</Text>
      </div>

      <Tabs defaultActiveKey="providers" size="large">
        <TabPane tab={<span><GlobalOutlined />Providers</span>} key="providers">
          <Card
            title="Payment Providers"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateProvider}>
                Add Provider
              </Button>
            }
          >
            <Table
              columns={providerColumns}
              dataSource={providers}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>

          {selectedProvider && providerDetails && (
            <Card
              title={`${selectedProvider.provider_name} Details`}
              style={{ marginTop: 16 }}
              extra={
                <Button onClick={() => setSelectedProvider(null)}>
                  Close
                </Button>
              }
            >
              <Tabs size="small">
                <TabPane tab="Credentials" key="credentials">
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddCredential(selectedProvider.id)}
                    >
                      Add Credential
                    </Button>
                  </div>
                  <Table
                    columns={credentialColumns}
                    dataSource={providerDetails.credentials}
                    rowKey="id"
                    size="small"
                    pagination={false}
                  />
                </TabPane>
                <TabPane tab="Fee Configuration" key="fees">
                  <Alert
                    message="Fee configurations are automatically loaded from provider setup"
                    type="info"
                    style={{ marginBottom: 16 }}
                  />
                  <Table
                    columns={[
                      { title: 'Payment Method', dataIndex: 'payment_method', key: 'payment_method' },
                      { title: 'Fee Type', dataIndex: 'fee_type', key: 'fee_type' },
                      { title: 'Flat Fee', dataIndex: 'flat_fee', key: 'flat_fee', render: (fee) => `Rp ${fee}` },
                      { title: 'Percentage', dataIndex: 'percentage_fee', key: 'percentage_fee', render: (fee) => `${(fee * 100).toFixed(2)}%` },
                      { title: 'Min Fee', dataIndex: 'minimum_fee', key: 'minimum_fee', render: (fee) => `Rp ${fee}` },
                    ]}
                    dataSource={providerDetails.fees}
                    rowKey="id"
                    size="small"
                    pagination={false}
                  />
                </TabPane>
              </Tabs>
            </Card>
          )}
        </TabPane>

        <TabPane tab={<span><SettingOutlined />System Settings</span>} key="settings">
          {Object.entries(settings).map(([category, categorySettings]) =>
            renderSettingsTable(categorySettings, category)
          )}
        </TabPane>
      </Tabs>

      {/* Provider Modal */}
      <Modal
        title={`${providerModal.mode === 'create' ? 'Create' : 'Edit'} Provider`}
        open={providerModal.visible}
        onCancel={() => setProviderModal({ visible: false, data: null, mode: 'create' })}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveProvider}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="provider_code"
                label="Provider Code"
                rules={[{ required: true, message: 'Provider code is required' }]}
              >
                <Input placeholder="e.g., duitku" disabled={providerModal.mode === 'edit'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="provider_name"
                label="Provider Name"
                rules={[{ required: true, message: 'Provider name is required' }]}
              >
                <Input placeholder="e.g., Duitku Arendo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="environment" label="Environment" initialValue="sandbox">
                <Select>
                  <Option value="sandbox">Sandbox</Option>
                  <Option value="production">Production</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Status" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="base_url" label="Base URL">
                <Input placeholder="https://api.provider.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="api_version" label="API Version">
                <Input placeholder="v1" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="supported_methods" label="Supported Methods">
            <Select mode="tags" placeholder="Enter payment method codes">
              <Option value="VA">Virtual Account</Option>
              <Option value="FT">Fund Transfer</Option>
              <Option value="BT">Bank Transfer</Option>
            </Select>
          </Form.Item>

          <Form.Item name="settings" label="Settings (JSON)">
            <TextArea rows={4} placeholder='{"timeout": 30, "retry_count": 3}' />
          </Form.Item>
        </Form>
      </Modal>

      {/* Credential Modal */}
      <Modal
        title="Add Credential"
        open={credentialModal.visible}
        onCancel={() => setCredentialModal({ visible: false, providerId: null })}
        onOk={() => credentialForm.submit()}
      >
        <Form form={credentialForm} layout="vertical" onFinish={handleSaveCredential}>
          <Form.Item
            name="credential_key"
            label="Credential Key"
            rules={[{ required: true, message: 'Credential key is required' }]}
          >
            <Input placeholder="e.g., api_key, merchant_code" />
          </Form.Item>

          <Form.Item
            name="credential_value"
            label="Credential Value"
            rules={[{ required: true, message: 'Credential value is required' }]}
          >
            <Input.Password placeholder="Enter credential value" />
          </Form.Item>

          <Form.Item name="environment" label="Environment" initialValue="sandbox">
            <Select>
              <Option value="sandbox">Sandbox</Option>
              <Option value="production">Production</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Setting Modal */}
      <Modal
        title="Edit Setting"
        open={settingModal.visible}
        onCancel={() => setSettingModal({ visible: false, data: null })}
        onOk={() => settingForm.submit()}
      >
        <Form form={settingForm} layout="vertical" onFinish={handleSaveSetting}>
          <Form.Item name="setting_value" label="Value" rules={[{ required: true }]}>
            {settingModal.data?.setting_type === 'boolean' ? (
              <Select>
                <Option value="true">True</Option>
                <Option value="false">False</Option>
              </Select>
            ) : settingModal.data?.setting_type === 'number' ? (
              <InputNumber style={{ width: '100%' }} />
            ) : (
              <Input />
            )}
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Config;